/**
 * Vespa Search API Integration
 * Provides product search and autocomplete functionality using the Vespa search app proxy
 */

/**
 * Convert product slug to Shopify handle
 * Removes -art-print and -canvas-print modifiers from slugs
 * Normalizes consecutive hyphens to match Shopify's automatic normalization
 * @param {string} slug - The product slug from Vespa
 * @returns {string} The Shopify handle
 * @example
 * slugToHandle('my-cool-art-print-2') // 'my-cool-2'
 * slugToHandle('my-cool-canvas-print-2') // 'my-cool-2'
 * slugToHandle('art---print') // 'art-print'
 */
function slugToHandle(slug) {
  if (!slug) return '';

  // Remove -art-print and -canvas-print modifiers, then normalize consecutive hyphens
  return slug
    .replace(/-art-print/g, '')
    .replace(/-canvas-print/g, '')
    .replace(/-+/g, '-');
}


/**
 * Build Vespa search URL for product search
 * @param {string} searchTerm - The search query
 * @param {object} options - Search options
 * @param {string} [options.presentation] - Presentation mode
 * @param {string} [options.productSummary] - Product summary mode (default: 'slug_only')
 * @param {string} [options.sort] - Sort order
 * @param {string} [options.categoryFilters] - Category filters
 * @param {number} [options.limit] - Maximum number of results
 * @param {number} [options.offset] - Offset for pagination
 * @param {number} [options.page] - Page number for pagination
 * @param {number} [options.pageSize] - Page size for pagination
 * @returns {URL} The Vespa search URL
 */
function buildProductSearchUrl(searchTerm, options = {}) {
  const url = new URL('/apps/vespa-search', window.location.origin);

  url.searchParams.set('queryProfile', 'Shop');
  url.searchParams.set('shopType', 'search-v3');
  url.searchParams.set('input', searchTerm);
  url.searchParams.set('yql', 'select * from product where {defaultIndex: ""}userInput(@input) and userQuery();');

  // Default to slug_only for smaller response size
  url.searchParams.set('productSummary', options.productSummary || 'slug_only');

  // Optional parameters
  if (options.presentation) {
    url.searchParams.set('presentation', options.presentation);
  }
  if (options.sort) {
    url.searchParams.set('sort', options.sort);
  }
  if (options.categoryFilters) {
    url.searchParams.set('categoryFilters', options.categoryFilters);
  }

  // Pagination support - use either offset/hits or page/pageSize
  if (options.page !== undefined && options.pageSize !== undefined) {
    url.searchParams.set('page', options.page.toString());
    url.searchParams.set('pageSize', options.pageSize.toString());
  } else {
    if (options.offset !== undefined) {
      url.searchParams.set('offset', options.offset.toString());
    }
    if (options.limit !== undefined) {
      url.searchParams.set('hits', options.limit.toString());
    }
  }

  return url;
}

/**
 * Build autocomplete API URL
 * @param {string} searchTerm - The search query
 * @param {number} [limit=8] - Maximum number of suggestions (not used by new API, kept for signature compatibility)
 * @returns {URL} The autocomplete API URL
 */
function buildAutocompleteUrl(searchTerm, limit = 8) {
  const url = new URL('/apps/chat-agent/api/autocomplete', window.location.origin);

  url.searchParams.set('q', searchTerm);

  return url;
}

/**
 * Fetch product search results from Vespa
 * @param {string} searchTerm - The search query
 * @param {object} [options] - Search options
 * @param {number} [options.limit=8] - Maximum number of results
 * @returns {Promise<{handles: string[], slugs: string[], totalCount: number, error: string|null}>}
 */
export async function fetchProductSearch(searchTerm, options = {}) {
  try {
    console.log('[VespaSearch] Fetching product search for:', searchTerm, 'with options:', options);

    const url = buildProductSearchUrl(searchTerm, options);
    console.log('[VespaSearch] Product search URL:', url.toString());

    const response = await fetch(url.toString());
    console.log('[VespaSearch] Product search response status:', response.status);

    if (!response.ok) {
      throw new Error(`Vespa product search failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[VespaSearch] Product search response data:', data);

    // Extract total count from Vespa response
    const totalCount = data?.root?.fields?.totalCount || 0;
    console.log('[VespaSearch] Vespa total hit count:', totalCount);

    // Extract product slugs from response
    const children = data?.root?.children;
    if (!children || !Array.isArray(children) || children.length === 0) {
      console.warn('[VespaSearch] No products found in response');
      return { handles: [], slugs: [], totalCount, error: null };
    }

    console.log('[VespaSearch] Found', children.length, 'products in this response');

    // Extract slugs and convert to handles
    const results = children
      .map(child => {
        const slug = child.fields?.slug;
        if (!slug) return null;

        const handle = slugToHandle(slug);
        console.log('[VespaSearch] Converted slug:', slug, '→', handle);

        return { slug, handle };
      })
      .filter(result => result && result.handle);

    const handles = results.map(r => r.handle);
    const slugs = results.map(r => r.slug);

    console.log('[VespaSearch] Extracted', handles.length, 'product handles:', handles);

    return { handles, slugs, totalCount, error: null };
  } catch (error) {
    console.error('[VespaSearch] Error fetching product search:', error);
    return { handles: [], slugs: [], totalCount: 0, error: error.message };
  }
}

/**
 * Fetch autocomplete suggestions from the autocomplete API
 * @param {string} searchTerm - The search query
 * @param {number} [limit=8] - Maximum number of suggestions (not used by new API, kept for signature compatibility)
 * @returns {Promise<{suggestions: Array<{text: string, url: string}>, error: string|null}>}
 */
export async function fetchAutocompleteSuggestions(searchTerm, limit = 8) {
  try {
    console.log('[VespaSearch] Fetching autocomplete suggestions for:', searchTerm);

    const url = buildAutocompleteUrl(searchTerm, limit);
    console.log('[VespaSearch] Autocomplete URL:', url.toString());

    const response = await fetch(url.toString());
    console.log('[VespaSearch] Autocomplete response status:', response.status);

    if (!response.ok) {
      throw new Error(`Autocomplete API failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[VespaSearch] Autocomplete response data:', data);

    if (!data?.suggestions || data.suggestions.length === 0) {
      console.warn('[VespaSearch] No suggestions found in response');
      return { suggestions: [], error: null };
    }

    console.log('[VespaSearch] Found', data.suggestions.length, 'suggestions');

    // Map string suggestions to {text, url} format
    const suggestions = data.suggestions
      .map(text => {
        if (!text) return null;

        const searchUrl = new URL(Theme.routes.search_url, window.location.origin);
        searchUrl.searchParams.set('q', text);

        return {
          text,
          url: searchUrl.toString()
        };
      })
      .filter(suggestion => suggestion);

    console.log('[VespaSearch] Extracted', suggestions.length, 'suggestions:', suggestions);

    return { suggestions, error: null };
  } catch (error) {
    console.error('[VespaSearch] Error fetching autocomplete suggestions:', error);
    return { suggestions: [], error: error.message };
  }
}

/**
 * Combined search: fetch both products and suggestions in parallel
 * @param {string} searchTerm - The search query
 * @param {object} [options] - Search options
 * @returns {Promise<{products: object, suggestions: object}>}
 */
export async function fetchCombinedSearch(searchTerm, options = {}) {
  console.log('[VespaSearch] Fetching combined search for:', searchTerm);

  const [products, suggestions] = await Promise.all([
    fetchProductSearch(searchTerm, options),
    fetchAutocompleteSuggestions(searchTerm, options.suggestionsLimit || 8)
  ]);

  return { products, suggestions };
}

/**
 * Fetch collection products from Vespa
 * @param {string} collectionHandle - The collection handle (shop parameter)
 * @param {object} [options] - Search options
 * @param {number} [options.page=0] - Page number (0-based)
 * @param {number} [options.pageSize=24] - Page size
 * @returns {Promise<{handles: string[], slugs: string[], totalCount: number, error: string|null}>}
 */
export async function fetchCollectionProducts(collectionHandle, options = {}) {
  try {
    console.log('[VespaSearch] Fetching collection products for:', collectionHandle, 'with options:', options);

    const url = new URL('/apps/vespa-search', window.location.origin);

    // Set required parameters for collection query
    // Note: queryProfile is 'Shop' with capital S, page is 0-based
    url.searchParams.set('queryProfile', 'Shop');
    url.searchParams.set('shop', collectionHandle);
    url.searchParams.set('page', (options.page !== undefined ? options.page : 0).toString());
    url.searchParams.set('pageSize', (options.pageSize || 24).toString());

    console.log('[VespaSearch] Collection query URL:', url.toString());

    const response = await fetch(url.toString());
    console.log('[VespaSearch] Collection query response status:', response.status);

    if (!response.ok) {
      throw new Error(`Vespa collection query failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[VespaSearch] Collection query response data:', data);

    // Extract total count from Vespa response
    const totalCount = data?.root?.fields?.totalCount || 0;
    console.log('[VespaSearch] Vespa total hit count:', totalCount);

    // Extract product slugs from response
    const children = data?.root?.children;
    if (!children || !Array.isArray(children) || children.length === 0) {
      console.warn('[VespaSearch] No products found in collection response');
      return { handles: [], slugs: [], totalCount, error: null };
    }

    console.log('[VespaSearch] Found', children.length, 'products in this response');

    // Extract slugs and convert to handles
    const results = children
      .map(child => {
        const slug = child.fields?.slug;
        if (!slug) return null;

        const handle = slugToHandle(slug);
        console.log('[VespaSearch] Converted slug:', slug, '→', handle);

        return { slug, handle };
      })
      .filter(result => result && result.handle);

    const handles = results.map(r => r.handle);
    const slugs = results.map(r => r.slug);

    console.log('[VespaSearch] Extracted', handles.length, 'product handles:', handles);

    return { handles, slugs, totalCount, error: null };
  } catch (error) {
    console.error('[VespaSearch] Error fetching collection products:', error);
    return { handles: [], slugs: [], totalCount: 0, error: error.message };
  }
}

/**
 * Fetch products by brand/vendor using the discover API
 * @param {string} brand - The brand/vendor name
 * @param {object} [options] - Search options
 * @param {number} [options.page=0] - Page number (0-based, will be converted to 1-based for API)
 * @param {number} [options.pageSize=16] - Page size
 * @param {string} [options.excludeHandle] - Product handle to exclude from results
 * @returns {Promise<{products: Array, html: string, totalCount: number, hasMore: boolean, error: string|null}>}
 */
export async function fetchBrandProducts(brand, options = {}) {
  try {
    const page = (options.page || 0) + 1; // Convert to 1-based

    // Use the discover API via collections proxy
    // Add market param for localized pricing
    const url = new URL('/a/collections/api/discover', window.location.origin);
    url.searchParams.set('market', getMarketCode());

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        pageType: 'browse',
        collectionFilters: {
          artist: brand  // Maps to 'brands' attribute in Vespa
        },
        page,
        shop: window.Shopify?.shop || window.location.hostname
      })
    });

    if (!response.ok) {
      throw new Error(`Discover API brand search failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    const products = data?.products || [];
    const totalCount = data?.totalResults || 0;
    const totalPages = data?.totalPages || 1;
    const currentPage = data?.currentPage || 1;
    const hasMore = currentPage < totalPages;
    const html = data?.html?.productGrid || '';

    return { products, html, totalCount, hasMore, error: null };
  } catch (error) {
    console.error('[VespaSearch] Error fetching brand products:', error);
    return { products: [], html: '', totalCount: 0, hasMore: false, error: error.message };
  }
}

/**
 * Fetch products by tag using the discover API (searches keywords via query)
 * @param {string} tag - The tag to search for
 * @param {object} [options] - Search options
 * @param {number} [options.page=0] - Page number (0-based, will be converted to 1-based for API)
 * @param {number} [options.pageSize=16] - Page size
 * @param {string} [options.excludeHandle] - Product handle to exclude from results
 * @returns {Promise<{products: Array, html: string, totalCount: number, hasMore: boolean, error: string|null}>}
 */
export async function fetchTagProducts(tag, options = {}) {
  try {
    const page = (options.page || 0) + 1; // Convert to 1-based

    // Use the discover API via search proxy
    // Tags are searched via the 'q' filter which searches keywords field
    // Add market param for localized pricing
    const url = new URL('/a/search/api/discover', window.location.origin);
    url.searchParams.set('market', getMarketCode());

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        pageType: 'search',
        filters: {
          q: tag  // Searches default fieldset including keywords
        },
        page,
        shop: window.Shopify?.shop || window.location.hostname
      })
    });

    if (!response.ok) {
      throw new Error(`Discover API tag search failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    const products = data?.products || [];
    const totalCount = data?.totalResults || 0;
    const totalPages = data?.totalPages || 1;
    const currentPage = data?.currentPage || 1;
    const hasMore = currentPage < totalPages;
    const html = data?.html?.productGrid || '';

    return { products, html, totalCount, hasMore, error: null };
  } catch (error) {
    console.error('[VespaSearch] Error fetching tag products:', error);
    return { products: [], html: '', totalCount: 0, hasMore: false, error: error.message };
  }
}

/**
 * Get market code from Shopify country
 * Maps Shopify country codes to market codes used by pricing APIs
 * @returns {string} Market code (e.g., 'gb', 'us', 'eu')
 */
function getMarketCode() {
  const country = window.Shopify?.country?.toLowerCase();
  if (!country) return 'gb'; // Default to GB

  // Map country codes to market codes
  const countryToMarket = {
    'gb': 'gb', 'uk': 'gb',
    'us': 'us',
    'au': 'au',
    'ca': 'ca',
    'nz': 'nz',
    // EU countries map to 'eu' market
    'de': 'eu', 'fr': 'eu', 'es': 'eu', 'it': 'eu', 'nl': 'eu',
    'be': 'eu', 'at': 'eu', 'pt': 'eu', 'ie': 'eu', 'fi': 'eu',
    'gr': 'eu', 'lu': 'eu', 'mt': 'eu', 'cy': 'eu', 'sk': 'eu',
    'si': 'eu', 'ee': 'eu', 'lv': 'eu', 'lt': 'eu',
  };

  return countryToMarket[country] || 'gb';
}

/**
 * Fetch visually similar products using CLIP embeddings
 * @param {string} handle - Product handle to find similar products for
 * @param {object} [options] - Search options
 * @param {number} [options.page=0] - Page number (0-based)
 * @param {number} [options.limit=16] - Number of results per page
 * @param {number} [options.minDistance=0] - Minimum CLIP distance threshold
 * @returns {Promise<{products: Array, totalCount: number, hasMore: boolean, error: string|null}>}
 */
export async function fetchSimilarProducts(handle, options = {}) {
  try {
    const page = options.page || 0;
    const limit = options.limit || 16;
    const offset = page * limit;
    const minDistance = options.minDistance || 0;

    // Use the similar products API
    const url = new URL('/a/accounts/similar-products', window.location.origin);
    url.searchParams.set('handles', handle);
    url.searchParams.set('limit', limit.toString());
    url.searchParams.set('offset', offset.toString());
    url.searchParams.set('minDistance', minDistance.toString());
    url.searchParams.set('full', 'true'); // Request full product data
    url.searchParams.set('market', getMarketCode()); // Add market for localized pricing

    const response = await fetch(url.toString());

    if (!response.ok) {
      throw new Error(`Similar products API failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // The full=true response returns products in a different format
    // We need to normalize it to match the discover API format
    const rawProducts = data?.products || [];
    const products = rawProducts.map(p => normalizeSimiProductlarToDiscoverFormat(p));
    const totalCount = data?.total || products.length;
    const hasMore = data?.hasMore || false;

    return { products, totalCount, hasMore, error: null };
  } catch (error) {
    console.error('[VespaSearch] Error fetching similar products:', error);
    return { products: [], totalCount: 0, hasMore: false, error: error.message };
  }
}

/**
 * Normalize similar products API format to discover API format
 * @param {Object} product - Product from similar products API
 * @returns {Object} Normalized product matching discover API format
 */
function normalizeSimiProductlarToDiscoverFormat(product) {
  return {
    product_id: product.id || product.handle,
    title: product.title,
    url: product.url || `/products/${product.handle}`,
    image_url: product.imageUrl || product.image_url,
    from_price: product.from_price || product.price,
    from_compare_at_price: product.from_compare_at_price || null,
    base_pricing: product.base_pricing || null,
    mock_config: product.mock_config || null, // Now returned from API with configurator enrichment
    attributes: {
      like_count: { numbers: [0] } // Not available in similar products API
    },
    brand: product.artist,
    has_configurator: product.hasConfigurator || product.has_configurator,
  };
}

// Export the slug conversion utility for external use
export { slugToHandle };
